import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '@/components/auth/AuthContext';
import { normalizeMediaUrl } from '@/utils/media';

export function EditProfileSection() {
  const navigate = useNavigate();
  const { user, updateProfile, uploadResume, uploadProfileImage } = useAuth();

  const [name, setName] = useState<string>(user?.full_name || '');
  const [email, setEmail] = useState<string>(user?.email || '');
  const [phone, setPhone] = useState<string>(user?.phone_number || '');
  const [location, setLocation] = useState<string>('');
  const [bio, setBio] = useState<string>('');
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [resume, setResume] = useState<File | null>(null);
  const addCacheBust = (url?: string) => {
    if (!url) return '';
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}v=${Date.now()}`;
  };
  const [previewUrl, setPreviewUrl] = useState<string>(() => addCacheBust(normalizeMediaUrl((user as any)?.profileImage || (user as any)?.profile_image || (user as any)?.profile_image_url) || ''));

  useEffect(() => {
    const url = normalizeMediaUrl((user as any)?.profileImage || (user as any)?.profile_image || (user as any)?.profile_image_url) || '';
    setPreviewUrl(url ? addCacheBust(url) : '');
  }, [user]);

  const handleProfileImageSelect = async (file: File | null) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }
    try {
      const res = await uploadProfileImage(file);
      if (res?.imageUrl) {
        const normalized = normalizeMediaUrl(res.imageUrl) || res.imageUrl;
        setPreviewUrl(addCacheBust(normalized));
        setProfileImage(null);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to upload image');
    }
  };

  const handleSave = async () => {
    toast.loading('Saving your profile...', { id: 'save-profile' });
    try {
      const payload: any = {
        full_name: name,
        email,
        phone_number: phone,
        location,
        bio,
      };

      const resultUpdate = await updateProfile(payload);
      if (resultUpdate?.error) {
        toast.error(resultUpdate.error, { id: 'save-profile' });
        return;
      }

      if (profileImage) {
        const resultImg = await uploadProfileImage(profileImage);
        if (resultImg?.error) {
          toast.error(resultImg.error, { id: 'save-profile' });
          return;
        }
        if (resultImg?.imageUrl) {
          const normalized = normalizeMediaUrl(resultImg.imageUrl) || resultImg.imageUrl;
          setPreviewUrl(normalized);
        }
      }

      if (resume) {
        const resultResume = await uploadResume(resume);
        if (resultResume?.error) {
          toast.error(resultResume.error, { id: 'save-profile' });
          return;
        }
      }

      toast.success('Profile saved successfully!', { id: 'save-profile' });
      navigate('/jobseeker-dashboard');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save profile', { id: 'save-profile' });
    }
  };

  return (
    <div className="p-6">
      <Card className="max-w-3xl mx-auto">
        <CardHeader>
          <CardTitle>Edit Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
              {previewUrl ? (
                <img src={previewUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-gray-500 font-semibold text-xl">{(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div>
              <Label htmlFor="profileImage">Profile Image</Label>
              <Input id="profileImage" type="file" accept="image/*" onChange={(e) => handleProfileImageSelect(e.target.files?.[0] || null)} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="location">Location</Label>
              <Input id="location" value={location} onChange={(e) => setLocation(e.target.value)} />
            </div>
          </div>
          <div>
            <Label htmlFor="bio">Bio</Label>
            <Textarea id="bio" rows={3} value={bio} onChange={(e) => setBio(e.target.value)} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="resume">Resume</Label>
              <Input id="resume" type="file" accept=".pdf,.doc,.docx" onChange={(e) => setResume(e.target.files?.[0] || null)} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate('/jobseeker-dashboard')}>Cancel</Button>
            <Button onClick={handleSave}>Save Changes</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
